/**
 * Shared types and constants for SchoolMapReport multi-view dashboard
 */

// Zanzibar map constants
export const ZANZIBAR_CENTER: [number, number] = [-6.15, 39.20];
export const DEFAULT_ZOOM = 9;

// Level ID → canonical name (fallback when school_level is missing)
export const LEVEL_ID_MAP: Record<number, string> = {
  1: 'Primary',
  2: 'Secondary',
  3: 'Pre-Primary',
  4: 'Basic',
  5: 'Pre-Primary',
};

// Island/zone mapping by region name
export const ISLAND_REGIONS: Record<string, string[]> = {
  Unguja: ['Kaskazini Unguja', 'Kusini Unguja', 'Mjini Magharibi'],
  Pemba: ['Kaskazini Pemba', 'Kusini Pemba'],
};

// Level colors for markers and charts
export const LEVEL_COLORS: Record<string, string> = {
  'Pre-Primary': '#9C27B0',
  Primary: '#1976D2',
  Secondary: '#FF9800',
  Basic: '#4CAF50',
  Unknown: '#757575',
};

// Choropleth color scales
export const CHOROPLETH_COLORS = ['#E3F2FD', '#90CAF9', '#42A5F5', '#1E88E5', '#0D47A1'];
export const TEACHERS_COLORS = ['#FFF3E0', '#FFB74D', '#FF9800', '#F57C00', '#E65100'];
export const SCHOOLS_COLORS = ['#E8F5E9', '#A5D6A7', '#66BB6A', '#388E3C', '#1B5E20'];
export const PTR_COLORS = ['#388E3C', '#66BB6A', '#FFC324', '#FF9800', '#D32F2F'];
export const GPI_COLORS = ['#D32F2F', '#FF9800', '#388E3C', '#FF9800', '#D32F2F'];

export type ChoroplethIndicator = 'enrollment' | 'teachers' | 'schools' | 'ptr' | 'gpi';
export type ViewTab = 'map' | 'dashboard' | 'table' | 'comparison' | 'analysis';

export interface SchoolData {
  school_id: string;
  school_name: string;
  school_reg_number: string;
  school_type: string;
  level_id: number;
  school_level: string;
  education_level: string;
  current_enrollment: number;
  male_students: number;
  female_students: number;
  total_teachers: number;
  total_classes: number;
  total_streams: number;
  latitude: number | null;
  longitude: number | null;
  region: { region_id: string; region_name: string; region_code: string } | null;
  district: { district_id: string; district_name: string; district_code: string } | null;
  shehia: { shehia_id: string; shehia_name: string } | null;
  ward: { ward_id: string; ward_name: string; ward_code: string } | null;
}

export interface DistrictAgg {
  district_name: string;
  district_code: string;
  region_name: string;
  total_schools: number;
  total_students: number;
  male_students: number;
  female_students: number;
  total_teachers: number;
  ptr: number;
  gpi: number | null;
}

export interface StatsResult {
  total: number;
  government: number;
  private: number;
  levels: Record<string, number>;
  totalStudents: number;
  totalTeachers: number;
  maleStudents: number;
  femaleStudents: number;
  ptr: number;
  gpi: number | null;
  withGPS: number;
}

export interface FilterState {
  islandFilter: string;
  regionFilter: string;
  districtFilter: string;
  typeFilters: Record<string, boolean>;
  levelFilters: Record<string, boolean>;
}

// Coastal zone constants
export const COASTAL_ZONE_COLOR = '#0288D1';
export const COASTAL_ZONE_FILL = 'rgba(2, 136, 209, 0.15)';
export const COASTAL_THRESHOLD_KM = 5;

// Proximity / distance analysis types
export interface NearestNeighborResult {
  schoolId: string;
  nearestSchoolId: string;
  nearestSchoolName: string;
  distanceKm: number;
}

export interface CoastalResult {
  schoolId: string;
  distanceToCoastKm: number;
  isCoastalZone: boolean;
}

export interface DistanceFromSelected {
  schoolId: string;
  schoolName: string;
  distanceKm: number;
  level: string;
  enrollment: number;
  latitude: number;
  longitude: number;
}

export interface CoastalStats {
  schoolCount: number;
  totalStudents: number;
  maleStudents: number;
  femaleStudents: number;
  totalTeachers: number;
  ptr: number;
  percentOfAll: number;
}

export interface DistanceBin {
  range: string;
  label: string;
  count: number;
}

// Utility functions
export const getSchoolLevel = (school: SchoolData): string => {
  const level = (school.school_level || '').trim();
  if (level.toLowerCase() === 'pre-primary') return 'Pre-Primary';
  if (level.toLowerCase() === 'primary') return 'Primary';
  if (level.toLowerCase() === 'secondary') return 'Secondary';
  if (level.toLowerCase() === 'basic') return 'Basic';
  const levelId = typeof school.level_id === 'string' ? parseInt(school.level_id as any) : school.level_id;
  return LEVEL_ID_MAP[levelId] || 'Unknown';
};

export const getIsland = (school: SchoolData): string => {
  const regionName = school.region?.region_name || '';
  if (ISLAND_REGIONS.Pemba.includes(regionName)) return 'Pemba';
  if (ISLAND_REGIONS.Unguja.includes(regionName)) return 'Unguja';
  return '';
};

export const normalizeType = (type: string | null): string => {
  if (!type) return 'PUBLIC';
  const upper = type.toUpperCase();
  if (upper === 'PRIVATE') return 'PRIVATE';
  return 'PUBLIC';
};
